// Komissiya formasining SOF qismi — JSX'siz.
//
// ⚠️ NEGA ALOHIDA FAYL: bu yerdagi funksiyalar komponent EMAS va
// ular ikki ekranda ishlatiladi (muharrir oynasi + agent yaratish
// formasi). Ularni "CommissionFields.tsx" ichida qoldirish Vite'ning
// tez yangilanishini ("react-refresh") buzardi — fayl ham komponent,
// ham yordamchi eksport qilsa modul butunlay qayta yuklanadi.
//
// ⚠️ Maydonlarning O'ZI "CommissionFields.tsx" da.

import { Form } from 'antd'
import type { FormInstance } from 'antd'
import type { Dayjs } from 'dayjs'
import type { Money } from '@/shared/lib/money'
import type { CommissionKind, CommissionRuleDraft } from './agentApi'
import {
  commissionPercentBp,
  deadTierRows,
  toDraftTiers,
  validateTiers,
  type TierProblem,
  type TierRow,
} from './commissionTiers'

/**
 * Komissiya formasining qiymatlari.
 *
 * ⚠️ `percent` da `string` HAM BOR va bu tipni ROSTGA keltirish
 * (XSB-COM-001, `toPercentNumber` izohi): HTML maydon satr qaytaradi.
 * Ilgari bu yerda faqat `number` yozilgani uchun butun komissiya
 * kiritish yo'li jimgina ishlamay turgan edi.
 */
export interface CommissionFormValues {
  /** `undefined`/`null` — komissiya QO'YILMAYDI (yaratish formasida ixtiyoriy) */
  kind?: CommissionKind | null
  percent?: number | string | null
  fixedAmount?: Money | null
  tiers?: (TierRow | undefined)[]
  validFrom?: Dayjs | null
}

/**
 * Muharrirning boshlang'ich qiymatlari.
 *
 * ⚠️ SUKUT PUL QIYMATI YO'Q: foiz ham, summa ham, pog'ona chegarasi
 * ham BO'SH boshlanadi. Oldindan qo'yilgan raqam «kimdir shunday deb
 * qaror qilgan» degan yolg'on taassurot berardi va operator uni
 * o'zgartirmasdan saqlashi mumkin edi — natijasi to'g'ridan-to'g'ri
 * pulda.
 */
export const COMMISSION_MODAL_INITIAL: CommissionFormValues = { kind: 'PERCENT', tiers: [{}] }

/** Yaratish formasida komissiya IXTIYORIY — tur tanlanmagan holda boshlanadi. */
export const COMMISSION_OPTIONAL_INITIAL: CommissionFormValues = { tiers: [{}] }

/** Operator komissiya kiritdimi — bo'sh bo'lsa ikkinchi so'rov UMUMAN yuborilmaydi. */
export function isCommissionRequested(values: CommissionFormValues | undefined | null): boolean {
  return values?.kind === 'PERCENT' || values?.kind === 'FIXED' || values?.kind === 'TIER'
}

/**
 * Forma qiymatlaridan SIM shakli.
 *
 * ⚠️ MODAL VA YARATISH FORMASI SHU BITTA funksiyani chaqiradi
 * (senior+ §7: umumiy qism ajratiladi). Ikki nusxa bo'lsa bittasida
 * `percentToBp` yoki `tiers: []` unutilardi va ikki ekran bir xil
 * kiritishdan boshqa-boshqa qoida yozardi.
 */
export function toCommissionDraft(
  agentId: string,
  values: CommissionFormValues,
): CommissionRuleDraft {
  return { agentId, ...toCommissionRuleWithoutAgent(values) }
}

/**
 * Qoida draft'i AGENT ID SIZ — yaratish oqimi uchun.
 *
 * ⚠️ Yaratishda id BIRINCHI so'rovdan keyin tug'iladi, ya'ni uni
 * formadan olib bo'lmaydi. `toCommissionDraft` ga soxta id berib,
 * keyin ustidan yozish zanjirni ikki joyda «to'g'ri id qaysi» degan
 * savolga qoldirardi.
 */
export function toCommissionRuleWithoutAgent(
  values: CommissionFormValues,
): Omit<CommissionRuleDraft, 'agentId'> {
  const kind: CommissionKind = values.kind ?? 'PERCENT'
  return {
    kind,
    // Foiz FOIZDA kiritiladi, backend BAZIS PUNKTDA kutadi (250 = 2.5%).
    percentBp: kind === 'PERCENT' ? commissionPercentBp(values.percent) : null,
    fixedAmount: kind === 'FIXED' ? (values.fixedAmount ?? null) : null,
    // ⚠️ Manba — YUBORILAYOTGAN qiymatlar, kuzatilgan nusxa emas:
    // ikkalasi bir lahza farq qilsa, ekranda ko'ringan zinapoya bilan
    // simga ketgani boshqa bo'lardi.
    tiers: kind === 'TIER' ? toDraftTiers((values.tiers ?? []).map((row) => row ?? {})) : [],
    // ⚠️ Bo'sh = HOZIRDAN (SHARTNOMA §5), nol sana emas.
    validFrom: values.validFrom ? values.validFrom.toISOString() : null,
  }
}

export interface CommissionState {
  kind: CommissionKind | undefined
  problems: readonly TierProblem[]
  dead: readonly number[]
  tierRows: readonly TierRow[]
}

/**
 * Zinapoyaning TIRIK holati — YOZAYOTGANDA hisoblanadi.
 *
 * ⚠️ `Form.useWatch` dan, `onOk` ichidan EMAS (egasi qarori
 * 2026-08-19): qatorni ko'chirish ham darhol tekshirilishi kerak,
 * chegaralar qat'iy o'suvchi bo'lishi shart va ko'chirish aynan shu
 * shartni buzadigan amal.
 *
 * ⚠️ Forma NUSXASI bilan ishlaydi, kontekst bilan emas: chaqiruvchi
 * uni `<Form>` dan TASHQARIDA — `Modal` ning `okButtonProps` ida —
 * ishlatadi.
 */
export function useCommissionState<Values>(
  form: FormInstance<Values>,
  perTxnLimitMinor: number | null,
  prefix: readonly string[] = [],
): CommissionState {
  const kind = Form.useWatch<CommissionKind | undefined>([...prefix, 'kind'], form)
  const watchedTiers = Form.useWatch<(TierRow | undefined)[] | undefined>(
    [...prefix, 'tiers'],
    form,
  )

  /*
    ⚠️ `Form.List` bo'sh qator uchun `undefined` beradi (maydon hali
    tegilmagan). Uni tekshiruvga to'g'ridan-to'g'ri berish «hech qanday
    muammo yo'q» degan YOLG'ON javob berardi — bo'sh qator aynan
    muammo. Shuning uchun bo'sh obyektga aylantiriladi.
  */
  const tierRows: TierRow[] = (watchedTiers ?? []).map((row) => row ?? {})

  return {
    kind,
    tierRows,
    problems: kind === 'TIER' ? validateTiers(tierRows) : [],
    dead: kind === 'TIER' ? deadTierRows(tierRows, perTxnLimitMinor) : [],
  }
}
